/**
 * API Test Request Execution
 *
 * Core function that executes API test requests by integrating all utilities:
 * - Credential validation
 * - Header building
 * - URL construction
 * - Response formatting
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 5.8, 6.1, 6.2, 6.3, 6.4, 6.5,
 *               7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 9.1, 9.2, 9.3, 9.4, 9.5, 9.6, 9.7
 */

import type { ApiTestRequest, ApiTestResponse } from '../types';
import { validateCredentialFields } from './validateCredentials';
import { buildRequestHeaders } from './buildRequestHeaders';
import { buildRequestUrl } from './urlBuilder';

/**
 * Environment configuration for gateway URLs
 */
interface EnvironmentUrls {
  sandbox: string;
  production: string;
}

/**
 * Default gateway URLs with dynamic environment detection
 */
const isQasEnvironment =
  typeof window !== 'undefined' &&
  (window.location.origin.includes('tatusolutions.com') ||
    window.location.hostname.includes('tatusolutions.com'));

const DEFAULT_GATEWAY_URLS: EnvironmentUrls = {
  sandbox:
    import.meta.env.VITE_SANDBOX_GATEWAY_URL ||
    (isQasEnvironment
      ? 'https://api-gateway.tatusolutions.com/sandbox'
      : 'http://localhost:8000/sandbox'),
  production:
    import.meta.env.VITE_PRODUCTION_GATEWAY_URL ||
    (isQasEnvironment ? 'https://api-gateway.tatusolutions.com' : 'http://localhost:8000'),
};

/**
 * Send a test request to an API endpoint
 *
 * This function integrates all API testing utilities to:
 * 1. Validate credentials before sending
 * 2. Build request headers with authentication
 * 3. Construct request URL with parameters
 * 4. Measure response time
 * 5. Send HTTP request
 * 6. Parse and format response
 * 7. Handle errors gracefully
 *
 * @param request - The API test request configuration
 * @param gatewayUrls - Optional custom gateway URLs (defaults to environment variables)
 * @returns Promise resolving to the API test response
 *
 * @example
 * ```typescript
 * const response = await sendTestRequest({
 *   productId: 'prod-123',
 *   endpoint: { method: 'POST', path: '/v1/users', ... },
 *   apiKey: 'mk_live_abc123',
 *   externalCredentials: { username: 'user', password: 'pass' },
 *   requestBody: { name: 'John' },
 *   environment: 'sandbox'
 * });
 *
 * if (response.success) {
 *   console.log('Status:', response.statusCode);
 *   console.log('Body:', response.body);
 *   console.log('Time:', response.responseTime, 'ms');
 * } else {
 *   console.error('Error:', response.error);
 * }
 * ```
 *
 * Preconditions:
 * - request.apiKey is valid and non-empty
 * - request.endpoint is valid ParsedEndpoint
 * - request.externalCredentials contains all required fields
 * - Network connection is available
 *
 * Postconditions:
 * - Returns ApiTestResponse with status code, headers, body
 * - Response time is measured in milliseconds
 * - Timestamp is ISO 8601 format
 * - If request fails: success = false and error contains message
 * - If request succeeds: success = true and body contains parsed response
 * - No side effects on application state (read-only operation)
 */
export async function sendTestRequest(
  request: ApiTestRequest,
  gatewayUrls: EnvironmentUrls = DEFAULT_GATEWAY_URLS
): Promise<ApiTestResponse> {
  // Step 1: Validate credentials (Requirement 3.1, 3.2, 3.3)
  // Note: We validate that credentials are provided, but the actual required fields
  // should come from the product's external_auth_config. For now, we check that
  // credentials are not empty if they're expected.
  if (Object.keys(request.externalCredentials).length === 0) {
    return {
      success: false,
      statusCode: 0,
      statusText: 'Validation Error',
      headers: {},
      body: null,
      responseTime: 0,
      timestamp: new Date().toISOString(),
      error: 'Missing required credential fields: No credentials provided',
    };
  }

  const requiredFields = Object.keys(request.externalCredentials);
  const validation = validateCredentialFields(request.externalCredentials, requiredFields);

  if (!validation.valid) {
    // Return validation error without making request
    return {
      success: false,
      statusCode: 0,
      statusText: 'Validation Error',
      headers: {},
      body: null,
      responseTime: 0,
      timestamp: new Date().toISOString(),
      error: `Missing required credential fields: ${validation.missingFields.join(', ')}`,
    };
  }

  // Step 2: Build request headers (Requirements 4.1, 4.2, 4.3, 4.4)
  const headers = buildRequestHeaders(
    request.apiKey,
    request.externalAuthType,
    request.externalCredentials
  );

  // Step 3: Build request URL (Requirements 5.2, 5.3, 5.4, 5.5)
  const baseUrl = request.environment === 'sandbox' ? gatewayUrls.sandbox : gatewayUrls.production;

  const url = buildRequestUrl(
    baseUrl,
    request.endpoint.path,
    request.pathParams,
    request.queryParams
  );

  // Step 4: Prepare request body (Requirements 5.6, 5.7)
  let body: string | undefined;
  const methodsWithBody = ['POST', 'PUT', 'PATCH'];

  if (methodsWithBody.includes(request.endpoint.method) && request.requestBody) {
    try {
      body = JSON.stringify(request.requestBody);
    } catch (error) {
      // Invalid JSON in request body
      return {
        success: false,
        statusCode: 0,
        statusText: 'Invalid Request Body',
        headers: {},
        body: null,
        responseTime: 0,
        timestamp: new Date().toISOString(),
        error: `Failed to serialize request body: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  // Step 5: Send request with timing (Requirements 6.1, 6.2, 6.3, 6.4, 6.5)
  const startTime = performance.now();

  try {
    // Send HTTP request (Requirement 5.1, 5.8)
    const httpResponse = await fetch(url, {
      method: request.endpoint.method,
      headers,
      body,
    });

    const endTime = performance.now();
    const responseTime = Math.max(0, endTime - startTime); // Ensure non-negative (Requirement 6.4)

    // Step 6: Extract response headers (Requirement 7.2)
    const responseHeaders: Record<string, string> = {};
    httpResponse.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    // Step 7: Parse response body based on Content-Type (Requirements 7.3, 7.4, 9.7)
    const contentType = httpResponse.headers.get('content-type') || '';
    let responseBody: any;

    try {
      if (contentType.includes('application/json')) {
        // Parse as JSON (Requirement 7.3)
        responseBody = await httpResponse.json();
      } else {
        // Parse as text (Requirement 7.4)
        responseBody = await httpResponse.text();
      }
    } catch (parseError) {
      // JSON parsing fallback (Requirement 9.7)
      responseBody = await httpResponse.text();
    }

    // Step 8: Determine success flag (Requirements 7.5, 7.6)
    const success = httpResponse.status >= 200 && httpResponse.status <= 299;
    const error = success ? undefined : `HTTP ${httpResponse.status}: ${httpResponse.statusText}`;

    // Step 9: Build and return response (Requirement 7.1, 7.7)
    return {
      success,
      statusCode: httpResponse.status,
      statusText: httpResponse.statusText,
      headers: responseHeaders,
      body: responseBody,
      responseTime,
      timestamp: new Date().toISOString(), // ISO 8601 format (Requirement 7.7)
      error,
    };
  } catch (networkError) {
    // Step 10: Handle network errors gracefully (Requirements 9.1, 9.2, 9.3, 9.4, 9.6)
    const endTime = performance.now();
    const responseTime = Math.max(0, endTime - startTime); // Measure time even on error (Requirement 6.5)

    return {
      success: false,
      statusCode: 0, // Network error status code (Requirement 9.3)
      statusText: 'Network Error', // Network error status text (Requirement 9.3)
      headers: {}, // Empty headers on network error (Requirement 9.4)
      body: null, // Null body on network error (Requirement 9.4)
      responseTime,
      timestamp: new Date().toISOString(),
      error: networkError instanceof Error ? networkError.message : 'Unknown network error', // Error message from exception (Requirement 9.2)
    };
  }
}
